import React, { useState, useEffect, useRef } from 'react';
import useEventFilters from './hooks/useEventFilters';
import { Icon } from '../../shared/utils/icons';
import Hero from './Hero/Hero';
import Agenda from './Agenda/Agenda';
import MySessions from './Sidebar/MySessions';
import ForumActions from './Sidebar/ForumActions';
import FilterDrawer from './FilterDrawer/FilterDrawer';
import EventModal from './EventModal/EventModal';

const EventProgram = ({ program }) => {
    const { state, filtered, activeCount, chips, options, toggle, setQuery, removeChip, clearAll } =
        useEventFilters(program);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    // The sidebar always carries the Forum actions; MySessions renders itself away
    // when the visitor has no registrations.
    const mySessions = program?.mySessions || [];

    // Floating filter shows only once the hero filter has scrolled out of view.
    const filterRef = useRef(null);
    const [filterOffscreen, setFilterOffscreen] = useState(false);

    useEffect(() => {
        const el = filterRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(([e]) => setFilterOffscreen(!e.isIntersecting), { threshold: 0 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    // Escape closes the modal first, then the drawer.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (selected) setSelected(null);
            else setDrawerOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [selected]);

    // Lock body scroll while an overlay is open.
    useEffect(() => {
        const locked = drawerOpen || !!selected;
        document.body.style.overflow = locked ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [drawerOpen, selected]);

    return (
        <div className="lk-event-program lk-world-hero lk-world-hero--map-lg">
            <Hero
                query={state.query}
                setQuery={setQuery}
                activeCount={activeCount}
                chips={chips}
                onRemove={removeChip}
                onOpenFilters={() => setDrawerOpen(true)}
                searchRef={filterRef}
                chapters={options.chapters}
                activeChapters={state.chapters}
                onToggleChapter={(slug) => toggle('chapters', slug)}
            />

            <div className="page-wrap">
                <div className="main-layout">
                    <Agenda events={filtered} onOpen={setSelected} onClear={clearAll} />
                    <aside className="sidebar">
                        <MySessions sessions={mySessions} />
                        <ForumActions chapters={program?.chapters || []} links={program?.links || {}} />
                    </aside>
                </div>
            </div>

            <button
                className={`ep-fab${filterOffscreen ? ' is-visible' : ''}`}
                onClick={() => setDrawerOpen(true)}
                aria-label="Open filters"
            >
                <Icon name="filter" size={20} strokeWidth={2} />
                {activeCount > 0 && <span className="ep-fab__badge">{activeCount}</span>}
            </button>

            <FilterDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                options={options}
                state={state}
                toggle={toggle}
                clearAll={clearAll}
                count={filtered.length}
            />

            <EventModal event={selected} onClose={() => setSelected(null)} />
        </div>
    );
};

export default EventProgram;
